import { Box, MEDIA_QUERY, useResponsive } from '@bucketplace/design-system';
import { getVar, useTheme } from '@bucketplace/tokens';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import gsap from 'gsap';
import dynamic from 'next/dynamic';
import {
  Application,
  Assets,
  Container,
  Rectangle,
  Sprite,
  Texture,
} from 'pixi.js';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { REBRANDING_THEME } from '@/brand/common/style';
import { AnimationComponentRef } from '@/brand/hooks';
import { usePlatformInfo } from '@/promotions/hooks/usePlatformInfo';
import { CHEVRON_DOWN_MOTION, CLOUD_SECTION_ASSETS_URL } from '../constants';
import { Container as SectionContainer } from '../RebrandSectionContainer';
import { getRebrandStyle } from '../utils';

const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

// ===== Consts =====
const TOTAL_SPRITES = 500;

// 심볼 SVG 경로 (실제 경로로 교체)
const SYMBOL_SVG_URL = '/rebranding/images/symbol.svg';

// 바운딩 박스 대비 살짝 크게(테두리 맞춤) 보정 - end(최종) 크기에 영향
const SYMBOL_OVERSCALE = 1.3;

// 인트로 구간에서만 크게 보이게 (끝날 땐 1로 수렴 → end 크기는 동일)
const SYMBOL_INTRO_SCALE = 1; // 시작 배율
const INTRO_BEGIN = 0.0;
const TINT_BEGIN = 0.5;
const INTRO_LOCK = TINT_BEGIN; // 이 시점 이후 intro 배율 1로 고정

// 심볼이 틴트보다 약간 빨리 등장하도록
const SYMBOL_LEAD = 0;

// 틴트(오버레이) 타이밍
const TINT_END = 0.9;

// 사진/틴트 페이드아웃 구간
const MOSAIC_HIDE_BEGIN = 0.73;
const MOSAIC_HIDE_END = 0.8;

// "이동"만 멈추고 스케일은 계속 줄이기
const POSITION_FREEZE_T = Math.min(MOSAIC_HIDE_BEGIN, 0.68);

// ===== Utils =====
const clamp = (v: number, a: number, b: number): number =>
  Math.max(a, Math.min(b, v));
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

// 진행도 → 시그모이드(중심 0.25)
const sigmoid25 = (x: number): number => 1 / (1 + Math.exp(-12 * (x - 0.25)));

interface Position {
  x: number;
  y: number;
}
export interface AtlasEntry {
  origin: [number, number];
  target: [number, number];
  uvMin: [number, number];
  uvMax: [number, number];
  atlas: string;
  atlasImage: string;
  isPriority: boolean;
}

export interface CloudSectionProps {
  mobilePositions: AtlasEntry[];
  pcPositions: AtlasEntry[];
}

interface SpriteData {
  node: Container;
  img: Sprite;
  overlay: Sprite;
  start: Position;
  target: Position;
  startScale: number;
  endScale: number;
}

function makeAtlasName(currentIndex: number, baseIndex: number): string {
  return `atlas${baseIndex + currentIndex + 1}`;
}

export const CloudSection = forwardRef<
  AnimationComponentRef,
  CloudSectionProps
>(({ mobilePositions, pcPositions }, ref) => {
  const [isReady, setIsReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const canvasContainertRef = useRef<HTMLDivElement | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const atlasTexturesRef = useRef<Map<string, Texture>>(new Map());

  const appRef = useRef<Application | null>(null);
  const spritesLayerRef = useRef<Container | null>(null);
  const uiLayerRef = useRef<Container | null>(null); // 심볼 레이어

  const spriteDataRef = useRef<SpriteData[]>([]);
  const symbolSpriteRef = useRef<Sprite | null>(null);
  const symbolBaseScaleRef = useRef<number>(1);
  const symbolEndScaleRef = useRef<number>(1); // base 대비 엔드 배율(픽셀 고정 결과)

  const worldRef = useRef<Container | null>(null);

  // 최적화
  const lastProgressRef = useRef(-1);
  // const PROGRESS_THRESHOLD = 0.001;
  // const rafIdRef = useRef<number | null>(null);

  const { cssVar } = useTheme<typeof REBRANDING_THEME>();
  const { isMobile: isMobileDivice } = usePlatformInfo();
  const { breakpoint } = useResponsive({
    fallbackBreakpoint: 'mobile',
    populateOnInitialRender: true, // 초기 렌더링에서 breakpoint 설정
    preventSyncOnInitialRender: false, // 초기 렌더링 후 동기화 허용
  });
  const positionsData = useMemo(() => {
    const isPc = breakpoint === 'laptop' || breakpoint === 'desktop';
    return isPc ? pcPositions : mobilePositions;
  }, [breakpoint, mobilePositions, pcPositions]);

  // position 데이터로부터 전체 바운딩 박스를 미리 계산
  const computeFullBoundingBox = useCallback(() => {
    if (!positionsData.length) {
      return null;
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    // 실제 사용할 스프라이트 수만큼만 계산
    const items = positionsData.slice(0, TOTAL_SPRITES);

    for (const entry of items) {
      // UV 좌표로부터 실제 크기 추정 (atlas 텍스처 크기 기준)
      const [u0, v0] = entry.uvMin;
      const [u1, v1] = entry.uvMax;

      // 실제 atlas 크기에 맞춰 추정
      const isPc = breakpoint === 'laptop' || breakpoint === 'desktop';
      const estimatedAtlasSize = isPc ? 1024 : 2048;
      const spriteW = estimatedAtlasSize * (u1 - u0);
      const spriteH = estimatedAtlasSize * (v1 - v0);

      // 최종 크기 (endScale 적용)
      const finalW = spriteW * 0.07; // endScale과 동일
      const finalH = spriteH * 0.07;

      const halfW = finalW / 2;
      const halfH = finalH / 2;

      // target 위치 기준으로 바운딩 박스 계산
      const left = entry.target[0] - halfW;
      const right = entry.target[0] + halfW;
      const top = entry.target[1] - halfH;
      const bottom = entry.target[1] + halfH;

      if (left < minX) {
        minX = left;
      }
      if (right > maxX) {
        maxX = right;
      }
      if (top < minY) {
        minY = top;
      }
      if (bottom > maxY) {
        maxY = bottom;
      }
    }

    if (
      !isFinite(minX) ||
      !isFinite(maxX) ||
      !isFinite(minY) ||
      !isFinite(maxY)
    ) {
      return null;
    }

    return {
      width: maxX - minX,
      height: maxY - minY,
      minX,
      maxX,
      minY,
      maxY,
    };
  }, [positionsData, breakpoint]);

  // 모자이크의 최종 바운딩 박스에 심볼을 자동 피팅
  const computeSymbolBaseScale = useCallback(() => {
    const sp = symbolSpriteRef.current;
    if (!sp) {
      return;
    }

    // position 데이터로부터 미리 계산된 바운딩 박스 사용
    const bbox = computeFullBoundingBox();
    if (!bbox) {
      return;
    }

    const texW = sp.texture.width;
    const texH = sp.texture.height;
    if (!texW || !texH) {
      return;
    }

    const scaleFit =
      Math.min(bbox.width / texW, bbox.height / texH) * SYMBOL_OVERSCALE;
    symbolBaseScaleRef.current = scaleFit;

    sp.scale.set(scaleFit); // base
    sp.position.set(0, 0); // 스테이지 중심(현재 좌표계 기준)
  }, [computeFullBoundingBox]);

  const computeSymbolEndScalePx = useCallback(
    (containerScaleNow: number, endWidthPx?: number, endHeightPx?: number) => {
      const sp = symbolSpriteRef.current;
      const base = symbolBaseScaleRef.current || 1;
      if (!sp || !base) {
        return;
      }

      const texW = sp.texture.width || 1;
      const texH = sp.texture.height || 1;

      if (!endWidthPx && !endHeightPx) {
        // 기본: base 유지
        symbolEndScaleRef.current = 1;
        return;
      }

      const scaleByW = endWidthPx ? endWidthPx / texW : Infinity;
      const scaleByH = endHeightPx ? endHeightPx / texH : Infinity;

      // 둘 다 주면 더 작은 쪽으로 fit
      let absScale = Math.min(scaleByW, scaleByH);
      if (!isFinite(absScale)) {
        absScale = isFinite(scaleByW) ? scaleByW : scaleByH;
      }

      // hostRef가 전체를 축소/확대하므로, 실제 보이는 px를 맞추기 위해 보정
      const compensated = absScale / (containerScaleNow || 1);

      // base(bbox-fit) 대비 몇 배로 더 곱해야 하는지
      symbolEndScaleRef.current = compensated / base;
    },
    [],
  );

  // 진행도 → 상태 업데이트(+ 렌더)
  const updateSpritesAndRender = useCallback(
    (p: number): void => {
      const t = clamp(p, 0, 1);
      // if (Math.abs(t - lastProgressRef.current) < PROGRESS_THRESHOLD) return;
      lastProgressRef.current = t;

      // ===== 포지션/스케일 진행도 분리 =====
      // 포지션은 80%쯤에서 Freeze
      const posT = Math.min(t, POSITION_FREEZE_T);
      const positionProgress = clamp(sigmoid25(posT), 0, 1);

      // 스케일은 끝까지 진행
      const scaleProgress = clamp(sigmoid25(t), 0, 1);

      // 전체 컨테이너 축소
      const containerRaw = t < 0.3 ? 0 : (t - 0.3) / 0.7;
      const isPc = breakpoint === 'laptop' || breakpoint === 'desktop';
      const containerScaleRate = isPc ? 0.4 : 0.28;
      const containerProgress = easeInOutCubic(containerRaw);
      const containerScale = lerp(1.0, containerScaleRate, containerProgress);

      // 틴트
      const tintPhase = clamp((t - TINT_BEGIN) / (TINT_END - TINT_BEGIN), 0, 1);
      const tintK = easeInOutCubic(tintPhase);

      // 사진/틴트 페이드
      const hidePhase = clamp(
        (t - MOSAIC_HIDE_BEGIN) / (MOSAIC_HIDE_END - MOSAIC_HIDE_BEGIN),
        0,
        1,
      );
      // 초반 급하게 ↓ : 1 - easeOutCubic(h) = (1 - h)^3 (지금은 좀 더 완만하게 ^2)
      const mosaicFade = Math.pow(1 - hidePhase, 2);

      worldRef.current?.scale.set(containerScale);

      const symbolEndPx = getRebrandStyle('brandName', 'symbol', breakpoint);

      // >>> 픽셀 고정 엔드 스케일 갱신(매 프레임 컨테이너 스케일 변화에 맞춰 보정)
      computeSymbolEndScalePx(containerScale, symbolEndPx, symbolEndPx);

      // 심볼 : alpha & 인트로 스케일
      const symbol = symbolSpriteRef.current;
      if (symbol) {
        const symbolBegin = Math.max(0, TINT_BEGIN - SYMBOL_LEAD);
        const symbolEnd = TINT_END;
        const sPhase = clamp(
          (t - symbolBegin) / (symbolEnd - symbolBegin),
          0,
          1,
        );
        symbol.alpha = easeInOutCubic(sPhase);

        const introPhase = clamp(
          (t - INTRO_BEGIN) / (INTRO_LOCK - INTRO_BEGIN),
          0,
          1,
        );
        const introEase = 1 - Math.pow(1 - introPhase, 3); // easeOutCubic
        const introMul = lerp(
          SYMBOL_INTRO_SCALE,
          1,
          Number.isFinite(introEase) ? introEase : 1,
        );
        symbol.scale.set(symbolBaseScaleRef.current * introMul);
      }

      // 스프라이트 레이어 컨테이너 알파 (사진/틴트 묶음 페이드)
      const spritesLayer = spritesLayerRef.current;
      if (spritesLayer) {
        spritesLayer.alpha = mosaicFade;
      }

      // 개별 스프라이트 업데이트
      for (let i = 0; i < spriteDataRef.current.length; i++) {
        const d = spriteDataRef.current[i];

        // 위치는 Freeze된 positionProgress 사용
        const nx = lerp(d.start.x, d.target.x, positionProgress);
        const ny = lerp(d.start.y, d.target.y, positionProgress);

        // 스케일은 끝까지 진행하는 scaleProgress 사용
        const ns = lerp(d.startScale, d.endScale, scaleProgress);

        d.node.x = nx;
        d.node.y = ny;
        d.node.scale.set(ns);

        // per-sprite 알파에도 mosaicFade 적용
        const overlayAlpha = tintK * mosaicFade;
        const imageAlpha = 1 * (1 - tintK) * mosaicFade;
        d.overlay.alpha = overlayAlpha;
        d.img.alpha = imageAlpha;
      }

      // 렌더
      // if (rafIdRef.current !== null) return;
      // rafIdRef.current = requestAnimationFrame(() => {
      //   rafIdRef.current = null;
      //   appRef.current?.render();
      // });
      appRef.current?.render();
    },
    [breakpoint, computeSymbolEndScalePx],
  );

  useImperativeHandle(ref, () => ({
    createTimeline() {
      const tl = gsap.timeline({ defaults: { ease: 'none' } });

      // 1) 타이틀 애니메이션
      if (titleRef.current) {
        tl.fromTo(
          titleRef.current,
          { y: -20, scale: 1, opacity: 1, xPercent: -50, yPercent: -50 },
          {
            scale: 0.95,
            opacity: 0,
            duration: 0.5,
            xPercent: -50,
            yPercent: -50,
          },
          0,
        );
      }

      // 2) Pixi 진행도 구간 (0→1)
      const prog = { p: 0 };
      tl.to(
        prog,
        {
          p: 1,
          duration: 5, // 원래 섹션 길이에 맞춰 놓기
          onUpdate: () => updateSpritesAndRender(prog.p),
          onStart: () => updateSpritesAndRender(0),
          onComplete: () => updateSpritesAndRender(1),
        },
        0,
      );

      tl.to(
        containerRef.current,
        {
          opacity: 0,
          duration: 0,
        },
        '>',
      );

      return tl;
    },
  }));

  // Pixi 초기화 & 스프라이트/심볼 생성
  useEffect(() => {
    let destroyed = false;

    (async () => {
      const host = hostRef.current;
      if (!host) {
        return;
      }

      if (!appRef.current) {
        const app = new Application();

        await app.init({
          resizeTo: host,
          backgroundAlpha: 0,
          preferWebGLVersion: 2,
          webgl: {
            antialias: !isMobileDivice,
            powerPreference: isMobileDivice ? 'low-power' : 'high-performance',
          },
        });

        if (destroyed) {
          return;
        }
        host.appendChild(app.canvas);
        appRef.current = app;

        // Pixi 초기화 시
        const world = new Container();
        app.stage.addChild(world);
        const spritesLayer = new Container();
        world.addChild(spritesLayer);
        spritesLayerRef.current = spritesLayer;
        const uiLayer = new Container();
        world.addChild(uiLayer);
        uiLayerRef.current = uiLayer;
        worldRef.current = world;

        const centerStage = (): void => {
          app.stage.pivot.set(0, 0);
          app.stage.position.set(
            app.renderer.width / 2,
            app.renderer.height / 2,
          );
        };
        centerStage();
        app.renderer.on('resize', () => {
          centerStage();
          computeSymbolBaseScale();
          updateSpritesAndRender(Math.max(0, lastProgressRef.current));
        });

        app.ticker.stop(); // 수동 렌더
      }

      if (!positionsData.length) {
        return;
      }

      // 아틀라스 로드
      const requiredAtlases = new Set<string>();
      positionsData
        .slice(0, TOTAL_SPRITES)
        .forEach((entry) => requiredAtlases.add(entry.atlas));

      // Priority atlas 동기 로드 (렌더링 전에 필수 완료)
      const atlasTextures = atlasTexturesRef.current;
      const deviceType = ['mobile', 'tablet'].includes(breakpoint as any)
        ? 'mobile'
        : 'pc';
      const priorityAtlases =
        CLOUD_SECTION_ASSETS_URL.atlas[deviceType].priority;
      const normalAtlases = CLOUD_SECTION_ASSETS_URL.atlas[deviceType].normal;

      // 1. Priority atlas 동기 로드 (반드시 완료 후 진행)
      for (let i = 0; i < priorityAtlases.length; i++) {
        const url = priorityAtlases[i];
        const atlasName = makeAtlasName(i, 0);
        if (!atlasTextures.has(atlasName)) {
          try {
            const tex = await Assets.load<Texture>(url);
            atlasTextures.set(atlasName, tex);
          } catch (e) {
            console.error(`Failed to load priority atlas ${i}:`, e);
            return; // Priority 로드 실패시 중단
          }
        }
      }
      setIsReady(true);

      // Normal atlas 백그라운드 비동기 로드
      Promise.all(
        normalAtlases.map(async (url, i) => {
          const atlasKey = makeAtlasName(i, priorityAtlases.length);
          if (!atlasTextures.has(atlasKey)) {
            try {
              const tex = await Assets.load<Texture>(url);
              atlasTextures.set(atlasKey, tex);
            } catch (e) {
              console.error(`Failed to load normal atlas ${i}:`, e);
            }
          }
        }),
      ).then(() => {
        // Normal atlas 로드 완료 후 추가 스프라이트 렌더링
        if (!destroyed) {
          buildSprites();
          updateSpritesAndRender(Math.max(0, lastProgressRef.current));
        }
      });

      // 타일 빌드 함수
      const buildSprites = (): void => {
        const layer = spritesLayerRef.current;
        if (!layer) {
          return;
        }

        layer.removeChildren();
        spriteDataRef.current = [];

        const items = positionsData.slice(0, TOTAL_SPRITES);
        for (let i = 0; i < items.length; i++) {
          const e = items[i];
          const atlasTex = atlasTextures.get(e.atlas);
          if (!atlasTex) {
            continue;
          }

          const [u0, v0] = e.uvMin;
          const [u1, v1] = e.uvMax;
          const frame = new Rectangle(
            atlasTex.width * u0,
            atlasTex.height * (1 - v1),
            atlasTex.width * (u1 - u0),
            atlasTex.height * (v1 - v0),
          );
          const tex = new Texture({ source: atlasTex.source, frame });

          const node = new Container();
          node.x = e.origin[0];
          node.y = e.origin[1];
          node.scale.set(1.0);
          node.eventMode = 'none';

          const img = new Sprite(tex);
          img.anchor.set(0.5);
          img.alpha = 1;
          node.addChild(img);

          const overlay = new Sprite(Texture.WHITE);
          overlay.anchor.set(0.5);
          overlay.width = frame.width;
          overlay.height = frame.height;
          overlay.tint = 0x00a1ff;
          overlay.alpha = 0;
          node.addChild(overlay);

          layer.addChild(node);

          spriteDataRef.current.push({
            node,
            img,
            overlay,
            start: { x: e.origin[0], y: e.origin[1] },
            target: { x: e.target[0], y: e.target[1] },
            startScale: 1.0,
            endScale: 0.07,
          });
        }
      };

      // Priority atlas로 초기 스프라이트 빌드
      buildSprites();

      // 심볼 로드/생성
      if (uiLayerRef.current && !symbolSpriteRef.current) {
        try {
          const symbolEndPx = getRebrandStyle(
            'brandName',
            'symbol',
            breakpoint,
          );
          const dpr = Math.min(window.devicePixelRatio || 1, 2);
          // 최종 보이는 px * DPR 만큼의 래스터 크기로 SVG를 로드 (가로 기준)
          const targetRasterWidth = Math.ceil(symbolEndPx * dpr);

          const symTex = await Assets.load<Texture>({
            src: SYMBOL_SVG_URL,
            data: {
              resourceOptions: { width: targetRasterWidth },
            },
          });

          const sp = new Sprite(symTex);
          sp.anchor.set(0.5);
          sp.alpha = 0; // 틴트 타이밍 전까지 숨김
          sp.eventMode = 'none';
          uiLayerRef.current.addChild(sp);
          symbolSpriteRef.current = sp;

          // 타일 바운딩 박스로 심볼 base 스케일 산출
          computeSymbolBaseScale();
        } catch (e) {
          console.error('Failed to load symbol svg:', e);
        }
      }

      // 첫 프레임
      updateSpritesAndRender(0);
      appRef.current?.render();
    })();

    return () => {
      destroyed = true;
    };
  }, [
    positionsData,
    isMobileDivice,
    updateSpritesAndRender,
    computeSymbolBaseScale,
    breakpoint,
  ]);

  return (
    <SectionContainer
      ref={containerRef}
      opacity={1}
      position="relative"
      css={css`
          canvas {
            pointer-events: none;
          }
        `}
    >
      <TitleContainer
        ref={titleRef}
        css={{
          color: getVar(cssVar('color-text-primary')),
        }}
      >
        <Title>{'어서오세요\n새로운 오늘의집'}</Title>
        <Box
          w={{
            mobile: 36,
            tablet: 36,
            laptop: 60,
          }}
          h={{
            mobile: 36,
            tablet: 36,
            laptop: 60,
          }}
        >
          <Lottie
            animationData={CHEVRON_DOWN_MOTION}
            loop
            autoplay
            width={36}
            height={36}
            css={{
              display: 'flex',
            }}
          />
        </Box>
      </TitleContainer>
      <Box ref={canvasContainertRef} position="relative" w="100vw" h="100vh">
        <Box position="fixed" top={0} h="100vh" w="100%">
          <Box
            ref={hostRef}
            w="100vw"
            h="100vh"
            opacity={0}
            css={
              isReady
                ? css`
                opacity: 1;
                transition: opacity 0.4s ease-out, visibility 0s linear;
              `
                : undefined
            }
          />
        </Box>
      </Box>
    </SectionContainer>
  );
});

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 18px;
  position: absolute;
  top: calc(50% + 27px);
  left: 50%;
  width: 100%;
  opacity: 0;

  ${MEDIA_QUERY.min(
    'laptop',
    css`
    font-size: 80px;
    gap: 30px;
    top: calc(50% + 45px);
  `,
  )}
`;

const Title = styled.p`
  text-align: center;
  font-size: 38px;
  font-weight: 700;
  line-height: 130%;
  white-space: pre-line;

  ${MEDIA_QUERY.min(
    'laptop',
    css`
    font-size: 80px;
  `,
  )}
`;
